import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Copy, Info } from 'lucide-react';
import { ACTIONS } from './ruleBuilderData';
import { DraggableCondition } from './RuleBuilderParts';

export const RuleBuilderSections = ({
  activeSection, setActiveSection,
  ruleData, updateField, generateRuleKey,
  conditionLogic, setConditionLogic, conditions,
  updateCondition, removeCondition, moveCondition, variables,
  addCondition, updateAction, removeAction, addAction,
  SEVERITIES,
}) => {
  return (
    <>
      {/* Section tabs */}
      <div className="flex gap-2 border-b pb-2">
        {['basic', 'conditions', 'actions', 'messages', 'filters'].map(section => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeSection === section
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {{
              basic: '📋 Informations',
              conditions: '🔀 Conditions',
              actions: '⚡ Actions',
              messages: '💬 Messages',
              filters: '🎯 Filtres'
            }[section]}
          </button>
        ))}
      </div>

      {/* Basic Info Section */}
      {activeSection === 'basic' && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clé de la règle *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={ruleData.rule_key}
                    onChange={(e) => updateField('rule_key', e.target.value.toUpperCase())}
                    placeholder="R-HARD-001"
                    className="font-mono"
                  />
                  <Button variant="outline" size="sm" onClick={generateRuleKey}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Sévérité *</Label>
                <div className="flex gap-2 mt-1">
                  {SEVERITIES.map(sev => (
                    <button
                      key={sev.value}
                      onClick={() => updateField('severity', sev.value)}
                      className={`flex-1 p-2 rounded-lg border-2 transition-all ${
                        ruleData.severity === sev.value
                          ? `border-${sev.color.replace('bg-', '')} ${sev.color} text-white`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg">{sev.icon}</span>
                      <span className="block text-xs font-medium">{sev.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Nom de la règle *</Label>
              <Input
                value={ruleData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Revenu minimum particulier"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={ruleData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Description détaillée de la règle..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Priorité (1=haute, 1000=basse)</Label>
                <Input
                  type="number"
                  value={ruleData.priority}
                  onChange={(e) => updateField('priority', parseInt(e.target.value) || 100)}
                  min={1}
                  max={1000}
                  className="mt-1 w-32"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>Règle active</Label>
                <button
                  onClick={() => updateField('is_active', !ruleData.is_active)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    ruleData.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    ruleData.is_active ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conditions Section */}
      {activeSection === 'conditions' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Conditions d'application</CardTitle>
              <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => {
                    setConditionLogic('all');
                    updateField('conditions', { all: conditions });
                  }}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    conditionLogic === 'all' ? 'bg-white shadow text-blue-600' : 'text-gray-500'
                  }`}
                >
                  TOUTES (AND)
                </button>
                <button
                  onClick={() => {
                    setConditionLogic('any');
                    updateField('conditions', { any: conditions });
                  }}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    conditionLogic === 'any' ? 'bg-white shadow text-blue-600' : 'text-gray-500'
                  }`}
                >
                  AU MOINS UNE (OR)
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              Glissez-déposez les conditions pour les réorganiser. La règle s'applique si 
              <strong>{conditionLogic === 'all' ? ' toutes ' : ' au moins une '}</strong>
              des conditions sont vraies.
            </p>

            {conditions.map((condition, index) => (
              <DraggableCondition
                key={index}
                condition={condition}
                index={index}
                onUpdate={updateCondition}
                onRemove={removeCondition}
                onMove={moveCondition}
                variables={variables}
                isFirst={index === 0}
                isLast={index === conditions.length - 1}
              />
            ))}

            <Button variant="outline" onClick={addCondition} className="w-full mt-4">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une condition
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Actions Section */}
      {activeSection === 'actions' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Actions à exécuter</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {ruleData.actions.map((action, index) => {
              const actionConfig = ACTIONS.find(a => a.value === action.action);
              const ActionIcon = actionConfig?.icon || Info;

              return (
                <div key={index} className={`p-4 rounded-lg border-2 ${actionConfig?.bg || 'bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg bg-white ${actionConfig?.color}`}>
                      <ActionIcon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 space-y-3">
                      <select
                        value={action.action}
                        onChange={(e) => updateAction(index, 'action', e.target.value)}
                        className="w-full border rounded-lg p-2 bg-white"
                      >
                        {ACTIONS.map(a => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>

                      {/* Cap specific fields */}
                      {action.action === 'cap' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Champ à plafonner</Label>
                            <select
                              value={action.field || ''}
                              onChange={(e) => updateAction(index, 'field', e.target.value)}
                              className="w-full border rounded-lg p-2 mt-1 bg-white"
                            >
                              <option value="">Sélectionner...</option>
                              {variables.filter(v => v.type === 'number').map(v => (
                                <option key={v.key} value={v.key}>{v.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Valeur maximum</Label>
                            <Input
                              type="number"
                              value={action.value || ''}
                              onChange={(e) => updateAction(index, 'value', parseFloat(e.target.value) || 0)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      )}

                      {/* Validation specific fields */}
                      {action.action === 'require_validation' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Validateur</Label>
                            <Input
                              value={action.validator || ''}
                              onChange={(e) => updateAction(index, 'validator', e.target.value)}
                              placeholder="college_fondateurs"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Niveau</Label>
                            <select
                              value={action.level || 'standard'}
                              onChange={(e) => updateAction(index, 'level', e.target.value)}
                              className="w-full border rounded-lg p-2 mt-1 bg-white"
                            >
                              <option value="standard">Standard</option>
                              <option value="enhanced">Renforcé</option>
                              <option value="committee">Comité</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Set value specific fields */}
                      {action.action === 'set_value' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Champ cible</Label>
                            <select
                              value={action.field || ''}
                              onChange={(e) => updateAction(index, 'field', e.target.value)}
                              className="w-full border rounded-lg p-2 mt-1 bg-white"
                            >
                              <option value="">Sélectionner...</option>
                              {variables.map(v => (
                                <option key={v.key} value={v.key}>{v.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Valeur</Label>
                            <Input
                              value={action.value || ''}
                              onChange={(e) => updateAction(index, 'value', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAction(index)}
                      disabled={ruleData.actions.length <= 1}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}

            <Button variant="outline" onClick={addAction} className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une action
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Messages Section */}
      {activeSection === 'messages' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Messages multilingues</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <span className="text-lg">🇫🇷</span> Message français *
              </Label>
              <Textarea
                value={ruleData.messages.fr}
                onChange={(e) => updateField('messages', { ...ruleData.messages, fr: e.target.value })}
                placeholder="Revenu insuffisant pour accéder à ce dispositif solidaire."
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <span className="text-lg">🇬🇧</span> Message anglais *
              </Label>
              <Textarea
                value={ruleData.messages.en}
                onChange={(e) => updateField('messages', { ...ruleData.messages, en: e.target.value })}
                placeholder="Insufficient income to access this solidarity-based scheme."
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <span className="text-lg">🇸🇦</span> Message arabe (RTL)
              </Label>
              <Textarea
                value={ruleData.messages.ar || ''}
                onChange={(e) => updateField('messages', { ...ruleData.messages, ar: e.target.value })}
                placeholder="الدخل غير كافٍ للوصول إلى هذه الآلية التضامنية."
                className="mt-1 text-right"
                dir="rtl"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters Section */}
      {activeSection === 'filters' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtres de périmètre</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-gray-500">
              Laissez vide pour appliquer à tous. Sélectionnez des valeurs pour restreindre le périmètre.
            </p>

            <div>
              <Label>Secteurs concernés</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Habitat', 'BTP', 'Agriculture', 'Commerce', 'Formation'].map(s => (
                  <Badge
                    key={s}
                    variant={ruleData.applies_to_sectors?.includes(s) ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-blue-100"
                    onClick={() => {
                      const current = ruleData.applies_to_sectors || [];
                      const updated = current.includes(s)
                        ? current.filter(x => x !== s)
                        : [...current, s];
                      updateField('applies_to_sectors', updated.length ? updated : null);
                    }}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Territoires concernés</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Guadeloupe', 'Martinique', 'Guyane', 'France Hexagonale', 'GCC', 'UAE'].map(t => (
                  <Badge
                    key={t}
                    variant={ruleData.applies_to_territories?.includes(t) ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-blue-100"
                    onClick={() => {
                      const current = ruleData.applies_to_territories || [];
                      const updated = current.includes(t)
                        ? current.filter(x => x !== t)
                        : [...current, t];
                      updateField('applies_to_territories', updated.length ? updated : null);
                    }}
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Types d'acteurs concernés</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Particulier', 'TPE', 'Exploitant', 'Association'].map(a => (
                  <Badge
                    key={a}
                    variant={ruleData.applies_to_actors?.includes(a) ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-blue-100"
                    onClick={() => {
                      const current = ruleData.applies_to_actors || [];
                      const updated = current.includes(a)
                        ? current.filter(x => x !== a)
                        : [...current, a];
                      updateField('applies_to_actors', updated.length ? updated : null);
                    }}
                  >
                    {a}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
